import { Injectable } from '@angular/core';
import { Invoice } from '@shared/classes/invoice.class';
import { SalesSubChannelTypesEnum, SelfOrderInvoiceStatusTypesEnum, StorageKeys } from '@shared/constants';
import { GetAllDataOptions } from '@core/services/db/db-dao.service';
import {
  query,
  group,
  or,
  isNull,
  Query,
  by,
  notEquals,
  oneOf,
} from '@paymash/capacitor-database-plugin';
import { SALES_CHANNEL_TYPES } from '@shared/constants/sales-channel-types';
import { SelfOrdersApiService } from '@core/services/api/self-orders-api.service';

import { InvoicesProvider } from '@core/services/resources/invoices-db-entity.provider';
import { firstValueFrom, Observable } from 'rxjs';
import { INVOICE_TYPES } from '@shared/constants/invoice-types';
import { LocalStorage } from '@core/services/utils/localstorage.utils';
import { SelfOrderTableDto } from '@shared/interfaces';
import { GastronomyTable } from '@shared/classes/gastronomy-table.class';
import { GastronomyHallsProvider } from '@core/services/resources';
import { GastronomyHall } from '@shared/classes/gastronomy-hall.class';

@Injectable()
export class SelfOrdersService {

  constructor(
    private selfOrderApi: SelfOrdersApiService,
    private invoicesProvider: InvoicesProvider,
    private gastronomyHallsProvider: GastronomyHallsProvider,
    private localStorage: LocalStorage
  ) { }

  public get localSettingsAutoPrintingOrder(): boolean {
    return !!this.localStorage.get(StorageKeys.settings.autoPrintingKitchenReceiptOnlineInvoice)
  }

  public set localSettingsAutoPrintingOrder(status: boolean) {
    if (status) {
      this.localStorage.set(StorageKeys.settings.autoPrintingKitchenReceiptOnlineInvoice, JSON.stringify(status));
    } else {
      this.localStorage.remove(StorageKeys.settings.autoPrintingKitchenReceiptOnlineInvoice);
    }
  }

  public get listOpenSelfOrderInvoicesFromLocalDB(): Observable<Invoice[]> {
    const queryParams: Query = query(
      {
        'ext_type': isNull(),
        'cancellationStatus': notEquals('CANCELLED'),
        'salesChannel': oneOf(SALES_CHANNEL_TYPES.WEBSHOP, SALES_CHANNEL_TYPES.ONLINE),
        'subChannel': SalesSubChannelTypesEnum.SELFORDER,
        'selfOrder.fulfillmentState': SelfOrderInvoiceStatusTypesEnum.OPEN,
        'invoiceType': INVOICE_TYPES.INVOICE
      },
      group({ ['deleted']: isNull() }, or({ ['deleted']: false }))
    );
    const options: GetAllDataOptions = {
      order: [by('fieldToSort', 'descending')],
    };
    return this.invoicesProvider.getListByParams(queryParams , options);
  }

  updatePreparationStatusFromCheckoutPage(invoice: Invoice, status: SelfOrderInvoiceStatusTypesEnum): void {
    const uuid: string = invoice?.selfOrder?.uuid;
    const fulfillmentState: SelfOrderInvoiceStatusTypesEnum = status;

    this.selfOrderApi.updatePreparationStatus({ uuid, fulfillmentState }).then();
  }

  updateSelfOrderService(currentItem: { properties: Invoice }): Invoice {
    return {
      ...currentItem?.properties,
      ...this.updateTableInfo(currentItem?.properties),
      paymentMethod: (currentItem?.properties?.paymentMethod && currentItem?.properties?.paymentMethod !== 'PREPAY') ? currentItem?.properties?.paymentMethod : null,
      selfOrder: {
        ...currentItem?.properties.selfOrder,
        uuid: currentItem?.properties.sourceUuid
      }
    } as Invoice;
  }

  async updateTableSelfOrder(uuid: string, table: string): Promise<SelfOrderTableDto> {
    const halls: GastronomyHall[] = await firstValueFrom(this.gastronomyHallsProvider.getList());
    const hall: GastronomyHall = halls.find((item: GastronomyHall): boolean => item.gastronomyTables.some((elem: GastronomyTable): boolean => elem.uuid === uuid));

    return uuid && hall ? {
      table,
      tableUuid: uuid,
      room: hall.name,
      roomUuid: hall.uuid
    } : {};
  }

  private updateTableInfo(invoice: Invoice): { gastronomyTableName: string,  gastronomyTable: { uuid: string } } {
    return {
      gastronomyTable: invoice?.selfOrder?.table ? { uuid: invoice.selfOrder.table.tableUuid } : null,
      gastronomyTableName: invoice.selfOrder?.table ? invoice?.selfOrder?.table.table : null,
    }
  }
}
